import { Fragment } from "react";
import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { useCms } from "@/lib/cms";
import { BACKEND_BASE } from "@/lib/backend";
import { openContactForm } from "@/lib/contactForm";
import "./AIPlatformSection.css";

const AI_IMG = "/images/ai-platform-section.png";

const FEATURES = [
  { eyebrow: "Education Market", title: "교육 시장 타겟", desc: "부트캠프·코딩 학원·대학교의 코드 리뷰 부담을 AI로 해소합니다." },
  { eyebrow: "Enterprise Trust", title: "기업급 신뢰성", desc: "민감한 코드는 격리 처리, 결과는 팀 대시보드에서 추적 가능합니다." },
  { eyebrow: "Continuous Learning", title: "지속 학습", desc: "리뷰 데이터를 기반으로 학습자별 약점을 자동 분석합니다." },
];

const TAGS = ["실시간 피드백", "학습 데이터 분석", "팀 대시보드", "GPT-5.2"];

export default function AIPlatformSection() {
  const { cms } = useCms();
  const aiImg = cms.media_ai_platform_image
    ? cms.media_ai_platform_image.startsWith("http")
      ? cms.media_ai_platform_image
      : `${BACKEND_BASE}${cms.media_ai_platform_image}`
    : AI_IMG;

  return (
    <section id="ai-platform" data-testid="ai-platform-section" className="ds-ai-platform-section">
      <div className="ds-ai-platform-inner">
        <div className="ds-ai-platform-grid">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.7, ease: [0.2, 0.7, 0.2, 1] }}
            className="ds-ai-hero"
          >
            <div className="ds-ai-hero-media">
              <img src={aiImg} alt="ai" data-testid="ai-platform-image" />
              <div className="ds-ai-hero-overlay" />
            </div>

            <div className="ds-ai-hero-code" aria-hidden="true">
              <div className="ds-ai-hero-code-dots">
                <span />
                <span />
                <span />
                <span className="ds-ai-hero-code-filename">review.ts</span>
              </div>
              <div><span className="kw">const</span> <span className="fn">review</span> = <span className="kw">await</span></div>
              <div style={{ paddingLeft: "0.5rem" }}>ai.<span className="fn">analyze</span>(code, {"{"}</div>
              <div style={{ paddingLeft: "1rem" }}>model: <span className="str">"gpt-5.2"</span>,</div>
              <div style={{ paddingLeft: "1rem" }}>depth: <span className="str">"detailed"</span></div>
              <div style={{ paddingLeft: "0.5rem" }}>{"}"});</div>
              <div className="muted" style={{ marginTop: "0.5rem" }}>→ 12 suggestions · 3 critical</div>
            </div>

            <div className="ds-ai-hero-content">
              <div>
                <span className="ds-ai-badge">
                  Coming Soon
                  <span className="ds-ai-badge-dot" />
                </span>
                <h2 data-testid="ai-platform-title" className="ds-ai-hero-title ds-cms-text whitespace-pre-line">
                  {cms.ai_platform_title}
                </h2>
                <p data-testid="ai-platform-body" className="ds-ai-hero-body ds-cms-text whitespace-pre-line">
                  {cms.ai_platform_body || "GPT 기반 자동 코드 리뷰 엔진. 부트캠프 · 대학교 · 교육 기관을 위한 AI 멘토링 SaaS로 곧 출시 예정입니다."}
                </p>
              </div>
              <div className="ds-ai-tags">
                {TAGS.map((t) => (
                  <span key={t} className="ds-ai-tag">
                    {t}
                  </span>
                ))}
              </div>
            </div>
          </motion.div>

          <div className="ds-ai-features">
            {FEATURES.map((c, i) => (
                <Fragment key={c.title}>
                  {i > 0 ? <div className="ds-ai-features-divider" aria-hidden="true" /> : null}
                  <motion.article
                    initial={{ opacity: 0, y: 16 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-60px" }}
                    transition={{ duration: 0.55, delay: i * 0.08, ease: [0.2, 0.7, 0.2, 1] }}
                    className="ds-ai-feature"
                    data-testid={`ai-feature-${i}`}
                  >
                    <header className="ds-ai-feature-head">
                      <p className="ds-ai-feature-eyebrow">{c.eyebrow}</p>
                      <h3 className="ds-ai-feature-title">{c.title}</h3>
                    </header>
                    <p className="ds-ai-feature-text">{c.desc}</p>
                  </motion.article>
                </Fragment>
              ))}

            <div className="ds-ai-features-divider" aria-hidden="true" />

            <motion.button
              type="button"
              data-testid="ai-platform-cta"
              className="ds-ai-feature-cta"
              initial={{ opacity: 0, y: 12 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.5, delay: 0.28, ease: [0.2, 0.7, 0.2, 1] }}
              onClick={openContactForm}
            >
              <span>사전 등록 / 도입 문의</span>
              <ArrowRight size={16} aria-hidden="true" />
            </motion.button>
          </div>
        </div>
      </div>
    </section>
  );
}
